import PropTypes from "prop-types";
import React, { memo } from "react";
import { htmlSanitize } from "app/utils/sanitize";
import parse from "html-react-parser";
import "./CustomFormattedMessage.scss";

// A `values` entry may be a plain value, or a handler function for tokens with a `{key:value}`
// argument (e.g. `currency`, see useDefaultTemplateHandlers) — called with that argument.
const resolveTemplateValue = (entry, tokenArgument) => {
	const resolved = typeof entry === "function" ? entry(tokenArgument) : entry;
	return resolved === null ? undefined : resolved;
};

const interpolateMessage = (msg, values = {}, enableHTMLFormatting) => {
	let newMessage = msg;
	if (typeof newMessage === "object") {
		newMessage = msg[0];
	}

	if (!newMessage) {
		return newMessage;
	}

	if (enableHTMLFormatting) {
		// Replace placeholders {key} or {key:value} with corresponding values
		return newMessage.replace(/{([^{}]+)}/g, (match, rawKey) => {
			const [key, tokenArgument] = rawKey.split(":");
			const value = resolveTemplateValue(values?.[key], tokenArgument);
			return typeof value !== "undefined" ? String(value) : match;
		});
	} else if (values) {
		// Split the message into parts and replace placeholders with values or React elements
		return newMessage
			.split(/({[^}]+})/g)
			.filter(Boolean)
			.map((part, index) => {
				const match = part.match(/{([^{}]+)}/);
				if (match) {
					const [key, tokenArgument] = match[1].trim().split(":");
					const value = resolveTemplateValue(values[key], tokenArgument);
					if (typeof value !== "undefined") {
						return React.isValidElement(value)
							? React.cloneElement(value, { key: index })
							: value;
					}
					return match[0];
				}
				return part;
			});
	}
	// Return the original message if no values are provided
	return newMessage;
};

const CustomFormattedMessage = ({
	message,
	className,
	values = {},
	children,
	enableHTMLFormatting = false,
}) => {
	const renderedMessage = interpolateMessage(message, values, enableHTMLFormatting);

	const renderedMessageComponent = enableHTMLFormatting ? (
		<div className="custom-formatted-message__rendered-message">
			{renderedMessage && parse(htmlSanitize(renderedMessage))}
			{children}
		</div>
	) : (
		<div className="custom-formatted-message__rendered-message">
			{renderedMessage}
			{children}
		</div>
	);
	return className ? (
		<div className={className}>{renderedMessageComponent}</div>
	) : (
		<>{renderedMessageComponent}</>
	);
};

CustomFormattedMessage.propTypes = {
	message: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
	values: PropTypes.object,
	className: PropTypes.string,
	children: PropTypes.any,
	enableHTMLFormatting: PropTypes.bool,
};

export default memo(CustomFormattedMessage);
